import {ApolloError, useQuery} from '@apollo/client';
import React, {useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import PredictionRunsList from 'model-hub/prediction-runs/PredictionRunsList';
import {
  PREDICTION_RUNS_POLL_INTERVAL_MS,
  splitPredictionRuns,
  type NormalizedRun,
} from 'model-hub/prediction-runs/splitPredictionRuns';

import type {
  GenerativePredictionJobsQuery,
  GenerativePredictionJobsQueryVariables,
} from './__generated/GenerativePredictionJobsQuery';
import {BoltzGenTargetType} from './GenerativeModelsConstants';
import {GENERATIVE_PREDICTION_JOBS_QUERY} from './GenerativeModelsQueries';

const BOLTZGEN_ICON_PATH = '/static/img/model_hub/boltz2.png';

export function useGetGenerativeModelRuns({
  currentEntityType,
  targetId,
}: {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
}): {
  pendingRuns: Array<NormalizedRun>;
  completedRuns: Array<NormalizedRun>;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
} {
  const queryVariables = {
    aaSequenceId: currentEntityType === ModelTypeConstants.PROTEIN ? targetId : undefined,
    moleculeId: currentEntityType === ModelTypeConstants.MOLECULE ? targetId : undefined,
  };

  const result = useQuery<GenerativePredictionJobsQuery, GenerativePredictionJobsQueryVariables>(
    GENERATIVE_PREDICTION_JOBS_QUERY,
    {
      variables: queryVariables,
      pollInterval: PREDICTION_RUNS_POLL_INTERVAL_MS,
    }
  );

  const runs: Array<NormalizedRun> = useMemo(
    () =>
      (result.data?.generativePredictionJobs ?? []).map((job) => ({
        id: job.id,
        status: job.status,
        createdAt: job.createdAt,
      })),
    [result.data]
  );

  const {pendingRuns, completedRuns} = useMemo(() => splitPredictionRuns(runs), [runs]);

  return {
    pendingRuns,
    completedRuns,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

export function GenerativeModelsRunsList({
  loading = false,
  pendingRuns = [],
  completedRuns = [],
  onCreateNewRun,
  onRunSelected = (_run: NormalizedRun) => {},
  disabledMessage,
}: {
  loading?: boolean;
  pendingRuns?: Array<NormalizedRun>;
  completedRuns?: Array<NormalizedRun>;
  onCreateNewRun: () => void;
  onRunSelected: (run: NormalizedRun) => void;
  disabledMessage?: string;
}): JSX.Element {
  return (
    <PredictionRunsList
      completedRuns={completedRuns}
      defaultCompletedIconPath={BOLTZGEN_ICON_PATH}
      disabledMessage={disabledMessage}
      loading={loading}
      onCreateNewRun={onCreateNewRun}
      onRunSelected={onRunSelected}
      pendingRuns={pendingRuns}
      title="BoltzGen Runs"
    />
  );
}
